import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { Link } from 'react-router-dom';

export default function CoreValues() {
  const values = [
    {
      icon: 'ri-shield-star-line',
      title: 'Integrity',
      description: 'We conduct every aspect of our business with honesty, transparency, and accountability. From sourcing to shipping, we honor our commitments and uphold the highest ethical standards across our supply chain.',
      color: 'green',
      image: 'https://readdy.ai/api/search-image?query=Professional%20handshake%20between%20Nigerian%20cocoa%20farmer%20and%20business%20executive%20showing%20trust%20and%20integrity%2C%20transparent%20business%20practices%20in%20agricultural%20setting%2C%20ethical%20sourcing%20documentation%20and%20certification%20processes%2C%20honest%20business%20relationships%2C%20warm%20professional%20lighting%20with%20emphasis%20on%20trust%20and%20reliability&width=600&height=400&seq=integrity-value&orientation=landscape'
    },
    {
      icon: 'ri-leaf-line',
      title: 'Sustainability',
      description: 'We are committed to long-term environmental stewardship, responsible sourcing, and positive social impact. Our sustainability practices are designed to protect ecosystems, empower farming communities, and build resilience across the cocoa value chain.',
      color: 'emerald',
      image: 'https://static.readdy.ai/image/ee7d0f3d5dd5d8644a11a0562535fbc0/42cc0ca7da0e0a236c0e385172527ba7.jpeg'
    },
    {
      icon: 'ri-award-line',
      title: 'Excellence',
      description: 'We pursue the highest standards in everything we do, from the quality of our cocoa to the professionalism of our people and the strength of our global partnerships. Consistency, precision, and pride in our work define the Olatunde\'s way.',
      color: 'amber',
      image: 'https://static.readdy.ai/image/ee7d0f3d5dd5d8644a11a0562535fbc0/6451849617205a47abe14a2f56742591.png'
    },
    {
      icon: 'ri-team-line',
      title: 'Partnership',
      description: 'We believe in the power of shared value. By fostering strong, transparent relationships with farmers, clients, regulators, and communities, we create a foundation of mutual trust, growth, and long-term impact across the cocoa value chain.',
      color: 'blue',
      image: 'https://readdy.ai/api/search-image?query=Community%20partnership%20meeting%20with%20cocoa%20farmers%2C%20diverse%20group%20of%20stakeholders%20collaborating%20around%20table%2C%20international%20business%20partners%20working%20with%20local%20farmers%2C%20shared%20value%20creation%20in%20agricultural%20community%2C%20collaborative%20relationships%20and%20mutual%20growth%2C%20warm%20community%20gathering&width=600&height=400&seq=partnership-value&orientation=landscape'
    },
    {
      icon: 'ri-lightbulb-line',
      title: 'Innovation',
      description: 'We embrace data, technology, and a culture of continuous improvement to drive traceability, boost productivity, and stay ahead of evolving global compliance standards. Innovation powers our progress, from farm to export.',
      color: 'purple',
      image: 'https://readdy.ai/api/search-image?query=Modern%20agricultural%20technology%20in%20cocoa%20farming%2C%20digital%20tablets%20and%20GPS%20mapping%20in%20field%2C%20innovative%20farming%20equipment%20and%20data%20collection%20systems%2C%20tech-enabled%20traceability%20solutions%2C%20modern%20agricultural%20innovation%2C%20farmers%20using%20cutting-edge%20technology%2C%20digital%20transformation%20in%20agriculture&width=600&height=400&seq=innovation-value&orientation=landscape'
    }
  ];

  const impactStats = [
    { number: '100%', label: 'Ethical Sourcing', icon: 'ri-shield-check-line' },
    { number: '500+', label: 'Farming Communities', icon: 'ri-community-line' },
    { number: '30+', label: 'Years of Excellence', icon: 'ri-star-line' },
    { number: '15+', label: 'Global Partners', icon: 'ri-global-line' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative py-32"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('https://static.readdy.ai/image/ee7d0f3d5dd5d8644a11a0562535fbc0/505731e60772be2d705e94bb781b9979.jpeg')`
        }}
      >
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Our Core Values
          </h1>
          <p className="text-xl md:text-2xl text-gray-200 max-w-5xl mx-auto leading-relaxed">
            At Olatunde International Limited, our operations are guided by a set of deeply rooted values, 
            principles that define who we are, how we work, and the future we're committed to building.
          </p>
        </div>
      </section>

      {/* Impact Statistics */}
      <section className="py-20 bg-green-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-green-800 mb-4">Values in Action</h2>
            <p className="text-gray-600 text-lg">Our commitment to these values drives measurable impact</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {impactStats.map((stat, index) => (
              <div key={index} className="text-center bg-white p-8 rounded-xl shadow-lg">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${stat.icon} text-2xl text-amber-600`}></i>
                </div>
                <h3 className="text-3xl font-bold text-green-800 mb-2">{stat.number}</h3>
                <p className="text-gray-600 font-medium">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="space-y-24">
            {values.map((value, index) => (
              <div key={index} className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}>
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <div className={`w-20 h-20 bg-${value.color}-100 rounded-full flex items-center justify-center mb-8`}>
                    <i className={`${value.icon} text-3xl text-${value.color}-600`}></i>
                  </div>
                  <h2 className="text-4xl font-bold text-green-800 mb-6">{value.title}</h2>
                  <p className="text-lg text-gray-700 leading-relaxed mb-8">{value.description}</p>
                  
                  <div className="flex items-center space-x-4">
                    <div className={`w-3 h-3 bg-${value.color}-600 rounded-full`}></div>
                    <span className={`text-${value.color}-600 font-semibold`}>Guiding Our Actions Daily</span>
                  </div>
                </div>
                
                <div className={index % 2 === 1 ? 'lg:col-start-1' : ''}>
                  <div className="relative">
                    <img 
                      src={value.image} 
                      alt={value.title}
                      className="w-full h-96 object-cover object-top rounded-2xl shadow-lg"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl"></div>
                    <div className="absolute bottom-6 left-6">
                      <div className={`w-12 h-12 bg-${value.color}-600 bg-opacity-90 rounded-full flex items-center justify-center`}>
                        <i className={`${value.icon} text-xl text-white`}></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Integration */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-green-800 mb-8">
              Living Our Values Every Day
            </h2>
            <p className="text-lg text-gray-700 mb-12 leading-relaxed">
              These five core values are not just words on paper—they are the foundation of every decision we make, 
              every relationship we build, and every step we take toward a more sustainable and prosperous cocoa industry.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-xl shadow-lg">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-checkbox-circle-line text-xl text-green-600"></i>
                </div>
                <h3 className="text-lg font-semibold text-green-800 mb-3">Daily Operations</h3>
                <p className="text-gray-600">Values guide every operational decision from farm to export</p>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-lg">
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-user-heart-line text-xl text-amber-600"></i>
                </div>
                <h3 className="text-lg font-semibold text-green-800 mb-3">Team Culture</h3>
                <p className="text-gray-600">Every team member embodies these values in their work</p>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-lg">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-handshake-line text-xl text-blue-600"></i>
                </div>
                <h3 className="text-lg font-semibold text-green-800 mb-3">Partnerships</h3>
                <p className="text-gray-600">Values foundation strengthens all stakeholder relationships</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="py-20 relative"
        style={{
          backgroundImage: `linear-gradient(rgba(34, 197, 94, 0.9), rgba(34, 197, 94, 0.9)), url('https://readdy.ai/api/search-image?query=Sunrise%20over%20Nigerian%20cocoa%20plantation%20symbolizing%20hope%20and%20future%20growth%2C%20golden%20light%20illuminating%20cocoa%20trees%20and%20farming%20community%2C%20inspirational%20agricultural%20landscape%2C%20bright%20future%20vision%20for%20sustainable%20farming%2C%20warm%20optimistic%20lighting%2C%20promise%20of%20continued%20excellence%20and%20growth&width=1920&height=600&seq=values-cta&orientation=landscape')`
        }}
      >
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Partner with Values-Driven Excellence
          </h2>
          <p className="text-xl text-green-100 mb-8 max-w-3xl mx-auto">
            When you choose Olatunde International Limited, you partner with a company whose 
            values align with sustainable growth, ethical practices, and lasting impact.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/why-choose-us">
              <Button variant="primary" size="lg" className="bg-amber-600 hover:bg-amber-700">
                Why Choose Us
                <div className="w-5 h-5 flex items-center justify-center ml-2">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </Button>
            </Link>
            <Link to="/sustainability">
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-green-800">
                Our Sustainability
                <div className="w-5 h-5 flex items-center justify-center ml-2">
                  <i className="ri-leaf-line"></i>
                </div>
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
